import type { Scene } from '@/types';

/**
 * Capítulo 4 — "El ojo abierto".
 *
 * Rework completo. Arco: descenso al Corazón de la Civilización,
 * revelación sobre el origen de la piedra y el papel involuntario de
 * Pipo como filtrador pre-activación, ritual del contenedor (con
 * requisitos de objetos/hechizos/stats), muerte del Cuervo según decisión,
 * preparación del clímax del Capítulo 5.
 */
export const CAP4_ESCENAS: Scene[] = [
  // ===========================================================================
  //  ACTO I — La partida
  // ===========================================================================
  {
    id: 'cap4_intro',
    capitulo: 4,
    titulo: 'El ojo abierto',
    background: { type: 'village', hint: 'amanecer frío y claro, la villa callada' },
    musica: 'mystical_loop',
    texto: [
      'Te despertás con la sensación de haber soñado algo importante y de haber tenido el sueño otros antes que vos. No te acordás del sueño. Te acordás del no-recuerdo, que es una forma más dolorosa de recordar.',
      'Afuera es temprano. La villa está callada de la manera en que se callan las villas donde alguien importante murió y no se llegó a velarlo como se debía. Urro, ausente físicamente, está presente en todas las miradas que te esquivan cuando pasás.',
      'Náyade te espera en la pasarela central. Tiene entre las manos un mapa viejo enrollado en un estuche de caña.',
      '—Es el único que me queda —te dice—. Se lo robamos a los Susurradores hace cincuenta años en una discusión vieja. Tiene el camino al Corazón. Cuando te lo doy, te doy también la obligación de devolverlo. Si no volvés, el mapa se entierra con vos. Así son los mapas viejos.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap4_iniciado', valor: true },
      { kind: 'stat', stat: 'vida', delta: 30 },
      { kind: 'stat', stat: 'mana', delta: 15 },
      { kind: 'item', itemId: 'mapa_corazon', delta: 1 },
    ],
    opciones: [
      {
        id: 'cap4_despedidas',
        texto: 'Aceptar el mapa. Ir a despedirte.',
        destino: 'cap4_despedidas',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap4_despedidas',
    capitulo: 4,
    titulo: 'Despedidas breves',
    background: { type: 'village', hint: 'villa despertándose, fogones encendidos' },
    texto: [
      'La villa no arma discursos para despedirte. Los que van a ir contigo están armando cosas. Los que no, están mirando.',
      '[if flag=sena_vuelve_con_tebo]Sena te espera al lado del puesto de Ibra. Tebo juega con unos palos secos. Sena va a ir con vos. No fue discusión: fue aviso. Ella va. Sabe cosas de La Sombra que los demás no saben.[/if]',
      '[if flag=karn_rescatado]Orón aparece sin que lo esperes. Trae un rifle de fabricación humana —herencia, decís sin decir—. No habla. Solo se une al grupo. Eso es su despedida de la villa y su saludo al viaje.[/if]',
      '[if flag=mara_aliada]Mara, desde la cocina común donde lleva días ayudando, te hace un gesto con la cabeza. No va: se queda como custodia de Náyade. Es la última decisión que le permite al pasado hacer sin deuda.[/if]',
      '[if flag=pipo_rescatado]Pipo, en la bandolera, no para de mirar hacia el noreste. Hacia donde vamos. No con miedo: con [glow color=mystic]anticipación[/glow]. Lo registrás. Lo guardás para pensarlo después. Hoy no.[/if]',
    ],
    opciones: [
      {
        id: 'cap4_ibra_ultimo',
        texto: 'Pasar por el puesto de Ibra antes de salir.',
        destino: 'cap4_ibra_ultimo',
        intencion: 'astuto',
      },
      {
        id: 'cap4_irse_sin_ibra',
        texto: 'Salir directo. Los mercaderes mercanderean. Vos vas al Corazón.',
        destino: 'cap4_camino_corazon',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap4_ibra_ultimo',
    capitulo: 4,
    background: { type: 'village', hint: 'Ibra empacando, menos cosas que nunca' },
    texto: [
      'Ibra está empacando. No es la quietud de siempre: está recogiendo hasta la bandeja donde deja las raíces de luz, hasta la franela con la que se limpia las manos. Un mercader que levanta hasta los trapitos es un mercader que no piensa volver en una temporada.',
      '—Me voy al sur —te dice, antes de que le preguntés—. Esto que se viene no es para un tipo como yo. Yo comercio con información y objetos. Lo que va a haber adelante no se intercambia: se sufre. No soy de sufrir. Soy de ganar plata.',
      '—Te dejo una última cosa. No es venta. Es regalo. Porque me caes mejor que la mayoría, y porque el recuerdo que me vendiste el otro día me salió más caro de lo que yo pensaba. Tomá.',
      'Te pone en la mano un frasquito chico con un líquido ámbar. —Es aceite de raíz mezclado con algo mío. Si en el Corazón hay un momento en el que necesitás ver algo que no se ve a ojo desnudo, lo frotás sobre el ojo izquierdo. Solo el izquierdo. Una vez.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'aceite_ibra', delta: 1 },
      { kind: 'flag', flagId: 'ibra_se_fue_al_sur', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap4_despedirse_ibra',
        texto: 'Asentir. Despedirte. No sabés si volvés a verlo.',
        destino: 'cap4_camino_corazon',
        intencion: 'empatico',
      },
    ],
  },

  // ===========================================================================
  //  ACTO II — El descenso
  // ===========================================================================
  {
    id: 'cap4_camino_corazon',
    capitulo: 4,
    titulo: 'El Corazón',
    background: { type: 'cliffs', hint: 'cresta noreste, piedra trabajada vieja' },
    texto: [
      'El Corazón de la Civilización no está en el corazón de ninguna parte. Está arriba, en una cresta noreste que los Susurradores visitaban cuando estaba de moda visitarlo, hace trescientos años. Desde entonces nadie sube. Los senderos están borrados pero la piedra trabajada está intacta: es la diferencia entre los caminos que labran los hombres y los caminos que labran las generaciones.',
      'Subiendo, cada cien pasos, encontrás columnas. Columnas de dos cuerpos humanos de alto, labradas con carpinchos que bailan un baile que no viste nunca. Algunos con anteojos de piedra, otros con libros, otros con tambores. Les falta nariz a la mitad de las figuras: los tres siglos hicieron lo suyo.',
      'Ninguna de las figuras mira hacia donde ustedes vienen. Todas miran hacia arriba. Hacia el pico de la cresta. Hacia donde, obviamente, está la cosa que vinieron a encontrar.',
    ],
    opciones: [
      {
        id: 'cap4_seguir_subiendo',
        texto: 'Seguir subiendo. No hace falta decidir si se entra: ustedes ya entraron.',
        destino: 'cap4_entrada_corazon',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap4_entrada_corazon',
    capitulo: 4,
    background: { type: 'ruins', hint: 'arco de piedra gigante, entrada a la ciudad antigua' },
    texto: [
      'En la cresta hay un arco. Un arco del ancho de cuatro hombres parados. Las dos columnas que lo sostienen están labradas con una misma inscripción repetida, en un idioma que vos no conocés y que [if flag=sena_vuelve_con_tebo]Sena tampoco[/if][if noflag=sena_vuelve_con_tebo]nadie contigo[/if].',
      '[if flag=pipo_rescatado]Pipo, en la bandolera, empieza a leerla. No la lee en voz alta: la [emph]registra[/emph]. Se le dilatan los ojos, se le para el pelaje del lomo, y respira dos veces muy profundo, como quien acaba de encontrar el aire al que pertenecía.',
      'Alguien entre los tuyos dice en voz muy baja: —Él sabe qué dice.[/if][if noflag=pipo_rescatado]Nadie lo entiende. Pero uno sabe que estas inscripciones tienen sentido: lo tienen en el peso de la piedra, en la precisión del corte. Los idiomas muertos pesan más que los vivos.[/if]',
      'Pasan el arco. Del otro lado hay una ciudad chica, toda de piedra roja, vacía hace siglos. Tres anillos concéntricos: el anillo exterior con casas, el medio con un edificio grande que sería una biblioteca o un templo, el interior con una cámara redonda al centro cuyas puertas están cerradas.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'dentro_corazon', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'cap4_biblioteca',
        texto: 'Ir al edificio grande. Náyade dijo: registros.',
        destino: 'cap4_biblioteca',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap4_biblioteca',
    capitulo: 4,
    titulo: 'El archivo',
    background: { type: 'ruins', hint: 'interior de un edificio de piedra con tablas grabadas en las paredes' },
    texto: [
      'La "biblioteca" no tiene libros como vos los conocés. Tiene tablas: tablas de piedra de un metro por cincuenta centímetros, colgadas de las paredes como cuadros, con inscripciones grabadas en el frente y —cuando vas a dar vuelta una— con imágenes grabadas en el dorso. Imágenes de escenas.',
      'El guardián de este archivo se quedó sentado contra una pared el día que abandonaron la ciudad. Vos no sabés eso. Lo intuís al verlo, por primera vez en la pared del fondo: un esqueleto de carpincho, enterito, con un collar de semillas secas todavía sobre las vértebras. Se sentó, cerró los ojos, no se movió más.',
      'Hay polvo por todos lados, y un silencio en el que los pasos de ustedes suenan obscenos.',
    ],
    opciones: [
      {
        id: 'cap4_leer_tablas',
        texto: 'Empezar a leer las tablas. [if flag=sena_vuelve_con_tebo]Sena descifra los símbolos capibáricos viejos.[/if][if noflag=sena_vuelve_con_tebo]Algunas las descifrás por comparación con las figuras del dorso.[/if]',
        destino: 'cap4_lectura_profunda',
        intencion: 'astuto',
      },
      {
        id: 'cap4_usar_aceite',
        texto: 'Frotar el aceite de Ibra sobre el ojo izquierdo. Ver qué se ve.',
        destino: 'cap4_vision_aceite',
        intencion: 'empatico',
        condiciones: [{ kind: 'item', itemId: 'aceite_ibra' }],
        alFallarCondicion: 'ocultar',
      },
    ],
  },

  {
    id: 'cap4_vision_aceite',
    capitulo: 4,
    titulo: 'Lo que se ve con un ojo',
    background: { type: 'mystical', hint: 'visión parcial: la mitad de la sala en color normal, la otra mitad en rojo' },
    texto: [
      'Frotás el aceite. El ojo izquierdo te arde dos segundos, y cuando abrís los dos a la vez, la sala se parte. El ojo derecho ve lo de siempre: esqueleto, tablas, polvo. El izquierdo ve [glow color=ember]líneas rojas[/glow] por toda la habitación: líneas que salen del piso, del techo, de las tablas mismas, todas apuntando a un punto al fondo de la sala que el otro ojo no distingue.',
      'Hay una tabla falsa. Una tabla que parece igual a las otras pero que, vista con este ojo, tiene un borde vibrante de rojo. La tocás. La tabla se corre. Detrás hay un nicho. Dentro del nicho, una caja pequeña de palo santo.',
      'Adentro de la caja, una segunda llave: más chica que la primera (si tuvieras una primera), con una espiral grabada a un lado y un carpincho minúsculo en el otro.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'llave_corazon', delta: 1 },
      { kind: 'flag', flagId: 'usó_aceite_ibra', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'cap4_leer_tras_llave',
        texto: 'Guardar la llave. Volver a las tablas.',
        destino: 'cap4_lectura_profunda',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap4_lectura_profunda',
    capitulo: 4,
    titulo: 'Los primeros Guardianes',
    background: { type: 'ruins', hint: 'tablas grabadas contra la pared, luz lateral' },
    texto: [
      'Las primeras cinco tablas son genealogía. Los primeros Guardianes, sus nombres, sus muertes. La sexta tabla cambia de tono: narra un descubrimiento.',
      '—Unos chicos, cavando un pozo, encontraron una piedra. La piedra les habló al tocarla. Les dijo que tenía hambre. Los chicos no sabían qué hacer con esa información y la llevaron a sus padres. Los padres la llevaron al Guardián.',
      '—El Guardián escuchó la piedra durante un mes entero. La piedra le contó de dónde venía (de antes), qué quería (comer), y qué pasaba si no se la alimentaba (se dormía mil años). Le contó también —y acá la tabla se vuelve extensa, porque los Guardianes entendieron que era clave— que la piedra, antes de comer, [glow color=mystic]sueña por otros[/glow]. Usa el cerebro del recipiente calibrado como antena. El recipiente filtra memorias, propone ideas, envía imágenes sin querer. Las personas cercanas al recipiente reciben esas señales y creen que son pensamientos propios. La piedra así se organiza a sí misma, a través de muchas personas, sin que ninguna lo sepa.',
      'Se te cae algo adentro.',
      '[if flag=pipo_rescatado]Pipo está dormido en la bandolera. Durmiendo, te mira la tabla. No abre los ojos. La ve igual. Vos lo sabés por el modo en que le tiembla la oreja izquierda.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_piedra_filtra', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap4_seguir_leyendo',
        texto: 'Seguir leyendo. Todavía falta entender cómo taparon la piedra.',
        destino: 'cap4_lectura_contenedor',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap4_lectura_contenedor',
    capitulo: 4,
    titulo: 'El contenedor',
    background: { type: 'ruins', hint: 'tabla final, escritura densa' },
    texto: [
      'La última tabla explica el método. Lo explica mal, con la vergüenza del que sabe que su método no es perfecto.',
      '—El Guardián de entonces armó un [emph]contenedor[/emph]. Un objeto compuesto: una pluma de un ave que come carroña (para absorber), una vela ritual bendecida durante siete noches (para quemar lento), y una memoria humana donada voluntariamente (para seducir a la piedra, que siempre prefirió lo humano a lo carpibárico). Los tres se unen frente a la piedra, se queman juntos, y la piedra come lo humano y se duerme. No muere. Solo se duerme. Quince siglos, tal vez. Tal vez menos.',
      '—El Guardián de entonces se dio cuenta, tarde, de que la "memoria humana donada" en realidad era la vida de un humano entero. El humano que donaba no volvía a ser el mismo. En muchos casos, no volvía a ser. La piedra cobraba.',
      '—Este es el precio del contenedor. Si se arma con las tres cosas, la piedra se duerme. El que donó la memoria, no.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_precio_contenedor', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap4_bajar_piedra',
        texto: 'Bajar a la cámara central. La piedra está ahí.',
        destino: 'cap4_cuervo_alli',
        intencion: 'valiente',
      },
    ],
  },

  // ===========================================================================
  //  ACTO III — La piedra
  // ===========================================================================
  {
    id: 'cap4_cuervo_alli',
    capitulo: 4,
    titulo: 'El Cuervo, cansado',
    background: { type: 'cave', hint: 'cámara circular iluminada con braseros, Cuervo sentado junto a una caja' },
    texto: [
      'La puerta de la cámara central está abierta. Por fuera parece cerrada: es un truco de la piedra, tiene un pivote invisible que gira al poner presión en un punto. El Cuervo está adentro. Sentado contra la pared, al lado de una caja de palo santo del tamaño de una almohada doblada. Está pálido. Está cerca del final.',
      '—Sabía que ibas a venir acá —te dice, sin moverse—. Y sabía que ibas a leer lo que yo leí hace quince años. Por lo menos en eso te ahorré el viaje a ciegas. Te queda una decisión y un trabajo. El trabajo es conmigo, el trabajo va a matarme, está bien. La decisión es tuya.',
      '—La piedra está acá. Las tres partes del contenedor existen en el mundo pero no todas están en esta cámara. Te toca saber si tenés las que hacen falta. Y decidir qué hacés con ellas.',
    ],
    opciones: [
      {
        id: 'cap4_inventario_contenedor',
        texto: 'Mostrarle lo que tenés.',
        destino: 'cap4_piedra_vista',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap4_piedra_vista',
    capitulo: 4,
    titulo: 'La piedra',
    background: { type: 'mystical', hint: 'piedra en caja abierta, espiral grabada, pulso rojo sutil' },
    texto: [
      'El Cuervo abre la caja con la punta de un pie. Adentro está la piedra. No es grande. Es del tamaño de una almohada doblada a la mitad. Tiene la espiral grabada en una cara.',
      'En la otra cara tiene algo que al principio te parece otra espiral y después entendés: es una espiral [shake]que se está empezando a desarmar[/shake]. El surco original está rodeado de nuevas líneas, cada vez más cortas, cada vez más apuradas. Algo —algo— viene saliendo. No está afuera todavía. No falta mucho.',
      '[if flag=pipo_rescatado]Pipo, en la bandolera, despierta. Mira la piedra. La piedra le devuelve la mirada. Eso no es una metáfora: entre los dos hay un hilo invisible que vibra, y vos lo ves vibrar con la periferia del ojo, y se te pone el estómago frío.[/if][if noflag=pipo_rescatado]El aire de la cámara cambia. Se pone espeso. Algo, del otro lado —algo que no está acá pero que está en alguna parte— se sacude al sentirte cerca. La piedra está llamando a su recipiente. Como sea, como pueda.[/if]',
    ],
    onEnter: [{ kind: 'flag', flagId: 'vio_la_piedra', valor: true }],
    opciones: [
      {
        id: 'cap4_cuervo_ritual_opcion',
        texto: 'Preguntarle al Cuervo cómo se arma el contenedor.',
        destino: 'cap4_cuervo_ritual',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap4_cuervo_ritual',
    capitulo: 4,
    titulo: 'Las tres piezas',
    background: { type: 'cave', hint: 'Cuervo extendiendo la mano, piedra vibrando apenas' },
    texto: [
      '—Pluma —dice—. Una pluma mía. La tenés o no la tenés.',
      '—Vela ritual. Bendecida. La vela que a veces Ibra le vende a los que no saben qué están comprando. La tenés o no la tenés.',
      '—Memoria humana. La más difícil. Tenés que donar un recuerdo central tuyo. No uno cualquiera: uno que te defina. La piedra no come recuerdos decorativos. Come el recuerdo que te hizo quien sos. Cuando lo dones, vas a poder seguir viviendo pero no vas a ser el mismo. Te lo advierto sin énfasis porque no quiero que sea dramático: quiero que sea claro.',
      '—Si no tenés las tres, hay que ir a buscar las que falten. No queda tiempo. La piedra está próxima. Si tenés las tres, armamos el contenedor y yo me ofrezco como recipiente alterno: la piedra entra en mí, no en Pipo, y yo —con el contenedor puesto— me dejo dormir con ella. Mil años. Tal vez menos.',
      '—Antes de que lo decidas, pensá una cosa: si hay otra opción, no la veo. La única otra opción es matar a Pipo antes de que se active. Si matás a Pipo, la piedra no tiene recipiente y se duerme sola. Pero vos vas a tener que matarlo, no yo.',
    ],
    opciones: [
      {
        id: 'cap4_ritual_con_items',
        texto: 'Ofrecer el contenedor: pluma + vela + memoria. Aceptás el precio.',
        destino: 'cap4_ritual_contenedor',
        intencion: 'empatico',
        condiciones: [
          { kind: 'item', itemId: 'pluma_cuervo' },
          { kind: 'item', itemId: 'vela_ritual' },
        ],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap4_ritual_con_magia',
        texto: 'Armar un contenedor alternativo: Vínculo Empático + Susurro del Río. Los Susurradores sabían otro método.',
        destino: 'cap4_ritual_magico',
        intencion: 'astuto',
        condiciones: [
          { kind: 'spell', hechizoId: 'vinculo_empatico' },
          { kind: 'spell', hechizoId: 'susurro_rio' },
        ],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap4_rechazar_ritual',
        texto: '[emph]"No voy a matar a Pipo. Tampoco voy a cambiar mi recuerdo central. Tiene que haber otra cosa."[/emph]',
        destino: 'cap4_sin_ritual',
        intencion: 'hostil',
      },
    ],
  },

  {
    id: 'cap4_ritual_contenedor',
    capitulo: 4,
    titulo: 'El ritual',
    background: { type: 'mystical', hint: 'fogón bajo con tres objetos alrededor de la piedra' },
    texto: [
      'Pones la pluma, la vela prendida y te sentás frente a la piedra. El Cuervo se sienta enfrente tuyo del otro lado. Te explica sin solemnidad lo que viene.',
      '—Tocá la piedra con la mano izquierda. Pensá el recuerdo. No lo cuentes en voz alta: pensalo. Ella lo va a sacar. Cuando sienta que está completo, va a saltar a mí. Mi parte la tengo hecha.',
      'Pones la mano. Cerrás los ojos. Buscás el recuerdo.',
      'No hay uno solo: hay varios. La primera vez que tu madre te llamó por tu nombre entero en voz baja cuando pensaba que no la oías. La cara de tu padre cuando te enseñó a afilar el primer cuchillo. La noche que te fuiste de tu casa sin despedirte, con dieciocho años, y tu hermana te dijo desde la cama "volvé". La primera vez que Pipo —cría, ciego todavía— se te quedó dormido en el pecho.',
      'La piedra elige. Por vos. Elige la noche de los dieciocho años. "Volvé". Lo elige porque es lo que te hizo quien sos: el que se fue una vez y no supo volver. La piedra lo succiona. Vos sentís el hueco. El hueco no duele.',
      'No recordás lo que la piedra se comió. Pero no recordás [emph]lo que fue[/emph]. Recordás que era algo. El hueco es más doloroso que el recuerdo mismo.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'pluma_cuervo', delta: -1 },
      { kind: 'item', itemId: 'vela_ritual', delta: -1 },
      { kind: 'flag', flagId: 'ritual_con_cuervo_hecho', valor: true },
      { kind: 'flag', flagId: 'franco_perdió_recuerdo', valor: true },
      { kind: 'stat', stat: 'empatia', delta: 2 },
      { kind: 'stat', stat: 'vida', delta: -15 },
      { kind: 'xp', cantidad: 120 },
    ],
    opciones: [
      {
        id: 'cap4_cuervo_sacrificio',
        texto: 'Mirar al Cuervo.',
        destino: 'cap4_cuervo_muerte',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap4_ritual_magico',
    capitulo: 4,
    titulo: 'El ritual susurrado',
    background: { type: 'mystical', hint: 'Franco canaliza dos hechizos a la vez' },
    texto: [
      'Le decís al Cuervo que vos sabés otro camino. Vega te enseñó cosas que él no tuvo tiempo de leer. Vos combinás Vínculo Empático —que toca la memoria ajena— con Susurro del Río —que calma lo tenso— y formás una jaula blanda, una red de palabras que no se dicen.',
      'El Cuervo te mira con respeto por primera vez. —No lo sabía. Los Susurradores lo guardaban bien. Adelante, entonces.',
      'Tocás la piedra con ambas manos. Pensás: "no te vamos a dar comida. Te vamos a dar compañía. Vos sos vieja. Vos estás cansada. Te vamos a acompañar mientras dormís." La piedra resiste, forcejea dentro tuyo. Pero no encuentra recuerdo que comer —porque no le diste, porque la red no se lo ofrece— y al no encontrar hambre, duda. Dudar es ya una forma de dormir.',
      'Se apaga. Sin estruendo. Como un fogón al que uno le tira tierra.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ritual_susurrado_hecho', valor: true },
      { kind: 'stat', stat: 'mana', delta: -15 },
      { kind: 'stat', stat: 'empatia', delta: 2 },
      { kind: 'stat', stat: 'astucia', delta: 1 },
      { kind: 'xp', cantidad: 150 },
    ],
    opciones: [
      {
        id: 'cap4_cuervo_sigue_vivo',
        texto: 'Mirar al Cuervo. Sigue vivo.',
        destino: 'cap4_cuervo_sobrevive',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap4_sin_ritual',
    capitulo: 4,
    background: { type: 'cave', hint: 'Cuervo asiente con tristeza' },
    texto: [
      '—Tu decisión es tu decisión —dice el Cuervo—. Pero entendé esto: sin contenedor, la piedra va a activarse. Pipo o yo somos las únicas opciones de recipiente disponible cerca. La piedra elige al más calibrado. Me elige a mí si estoy sano. Me eligió a Pipo cuando nació. Va a elegirlo a Pipo.',
      '—Si no haces nada, yo me voy a ofrecer igual. No estoy seguro de poder atraer a la piedra sin el contenedor. Probablemente falle. Probablemente se vaya a Pipo. Te aviso para que no digas después que no te avisé.',
      'La cámara empieza a temblar. No ustedes: la cámara. Afuera —de la ciudad, desde el arco— vienen ruidos que no pertenecen a este siglo. Disparos. Gritos humanos. El consorcio humano llegó.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap4_sin_ritual', valor: true },
      { kind: 'scheduleConsequence', consequenceId: 'trigger:cap5_intro:sin_contenedor' },
    ],
    opciones: [
      {
        id: 'cap4_a_combate_consorcio',
        texto: 'Salir. Algo más importante está pasando afuera.',
        destino: 'cap4_asalto_consorcio',
        intencion: 'valiente',
      },
    ],
  },

  // ===========================================================================
  //  ACTO IV — El fuego último
  // ===========================================================================
  {
    id: 'cap4_cuervo_muerte',
    capitulo: 4,
    titulo: 'Él solo',
    background: { type: 'mystical', hint: 'Cuervo sentado, piedra dentro de su pecho, luz tenue' },
    texto: [
      'El Cuervo se toca el pecho con una mano. La piedra ya no está en la caja: está adentro de él. Un bulto bajo la piel. Se le ve la silueta cuando respira.',
      '—Se siente como lo esperaba —dice, con una sonrisa que es casi risa—. Como una brasa adentro. Ella me está arrullando. Yo le estoy pidiendo perdón por cosas viejas. Van a ser mil años de diálogo entre nosotros. Mejor discusión que cualquier matrimonio que conocí.',
      '—Salí, Franco. Llevate al pibe. La piedra está durmiendo conmigo. Voy a quedarme acá hasta que me coma o me duerma. Tal vez las dos cosas. La vejez no distingue.',
      'Se sienta más cómodo contra la pared. Cierra los ojos.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cuervo_contenedor', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap4_salir_corazon',
        texto: 'Salir. Afuera algo se escucha.',
        destino: 'cap4_asalto_consorcio',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap4_cuervo_sobrevive',
    capitulo: 4,
    background: { type: 'cave', hint: 'Cuervo sorprendido, piedra dormida en la caja' },
    texto: [
      '—No esperaba esto —te dice el Cuervo, despacio, con una risa corta—. Llevo quince años creyendo que la única forma de apagarla era morirme yo. Y vos, con dos hechizos aprendidos en un mes, la dormís sin que nadie muera. [emph]No es justo[/emph]. En el buen sentido. No siempre lo justo es lo bueno.',
      'Cierra la caja. Se pone de pie con más dificultad de la que querría, apoyándose en la pared.',
      '—Afuera están los cazadores del consorcio humano. Vinieron a buscarnos. Saben que estamos acá por alguien. No sé por quién, pero vinieron. Salgamos.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cuervo_sobrevive', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap4_salir_con_cuervo',
        texto: 'Salir juntos.',
        destino: 'cap4_asalto_consorcio',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap4_asalto_consorcio',
    capitulo: 4,
    titulo: 'El consorcio',
    background: { type: 'fire', hint: 'la ciudad antigua invadida, humanos con rifles entrando' },
    texto: [
      'Afuera son doce hombres armados. Llevan rifles nuevos de fabricación industrial: balas, no pólvora negra. Liderándolos, un tipo alto de traje claro que no pertenece a la ciudad, a los bosques, a nada.',
      '—Ustedes se llevan la mercadería —dice, a sus hombres, sin mirarte—. Nosotros nos ocupamos de los demás. La piedra viene. Todo lo que esté al lado, queda.',
      'Te mira a vos ahora. Es el primer humano de "tu gente" que te mira a los ojos en siete años. No reconocés la cara, pero sí el apellido: cuando habla, su subordinado lo llama "señor Reyes". Una familia del sur, con tierras. [if flag=ibra_reveló_cazadores_pagos]Coincide con lo que te había dicho Ibra: los cazadores trabajan para gente como ésta.[/if]',
      'Hay un oso enorme a su lado. Un oso del fuego, con un collar de cuero entrenado por humanos. El consorcio alquila bestias, ahora. El mundo nuevo les enseñó el mundo viejo.',
    ],
    opciones: [
      {
        id: 'cap4_pelear_oso',
        texto: 'El oso primero. Si cae él, caen ellos.',
        destino: 'cap4_combate_oso',
        intencion: 'valiente',
      },
      {
        id: 'cap4_negociar_reyes',
        texto: '[emph]"Reyes. ¿Cuánto paga el que te paga?"[/emph]',
        destino: 'cap4_reyes_responde',
        intencion: 'astuto',
        condiciones: [{ kind: 'stat', stat: 'astucia', minimo: 5 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap4_reyes_responde',
    capitulo: 4,
    background: { type: 'fire', hint: 'Reyes sonríe, calculador' },
    texto: [
      '—Suficiente —te contesta—. Tanto como para no necesitar calcular. ¿Usted a quién le vende su servicio, señor…?',
      '—Franco. Franco G.',
      '—Franco. Mire. Yo no vine a hablar. Vine a cerrar un expediente. Si quiere negociar, le sugiero empezar explicándome por qué mi expediente debería no cerrarse hoy. Le doy treinta segundos. Menos, si pestañea feo.',
      'Los treinta segundos son los más largos de tu vida. No alcanzás a decidir la respuesta. Te salva una cosa que vos no previste: un rugido. El oso del fuego no estaba atado del modo adecuado a su entrenador.',
    ],
    opciones: [
      {
        id: 'cap4_a_combate_igual',
        texto: 'Aprovechar. A pelear.',
        destino: 'cap4_combate_oso',
        intencion: 'valiente',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap4_combate_oso',
    capitulo: 4,
    titulo: 'Oso del Fuego',
    background: { type: 'fire', hint: 'oso gigante contra ustedes en la plaza de la ciudad' },
    texto: [
      'El oso se suelta. Los humanos empiezan a disparar a todo lo que se mueve: a él, a ustedes, entre ellos mismos por error. Vos te concentrás en el oso.',
    ],
    combate: {
      enemyId: 'oso_del_fuego',
      onVictoria: 'cap4_tras_combate',
      onDerrota: 'cap4_fracaso_corazon',
      onHuida: 'cap4_huida_corazon',
    },
    opciones: [],
  },

  {
    id: 'cap4_tras_combate',
    capitulo: 4,
    background: { type: 'fire', hint: 'ciudad con humanos huyendo, oso caído' },
    texto: [
      'El oso cae. Reyes y los suyos ya no están: el caos los hizo retirarse. Quedan dos cuerpos humanos por tu mano (o por las balas que no supieron a dónde iban) y uno del consorcio vivo, arrodillado.',
      '—Reyes va a volver —dice el vivo, sin que le preguntés—. Con más. Va a traer a los del norte. Va a barrer todo este bosque. Lo único que quería la Sombra era que ustedes se enteraran a tiempo. Ese era todo su plan. Tizón sabía. El Cuervo sabía. Ustedes —se ríe mal— ustedes recién lo están entendiendo.',
      'Lo dejan vivo. No porque lo perdonen: porque no hay tiempo para matarlo ahora.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'consorcio_identificado', valor: true },
      { kind: 'flag', flagId: 'reyes_va_a_volver', valor: true },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap4_volver_villa_post',
        texto: 'Volver a la villa. Hay que convocar a las facciones.',
        destino: 'cap4_retorno_villa',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap4_fracaso_corazon',
    capitulo: 4,
    background: { type: 'fire', hint: 'Franco herido, retirada dolorosa' },
    texto: [
      'Caés. Te arrastran de vuelta por la salida norte. [if flag=pipo_rescatado]Pipo chilla por primera vez en días. No es un chillido pacífico: es una advertencia en una lengua que vos no sabés pero que los tuyos obedecen.[/if][if noflag=pipo_rescatado]Nadie chilla. Pero alguien, alguien que no estaba, pega un chillido desde lejos y te salva sin que lo veas.[/if]',
      'Escapan. Con pérdidas. La ciudad queda en llamas detrás tuyo: los humanos no se llevaron la piedra —el Cuervo [if flag=cuervo_contenedor]la tiene adentro, se está durmiendo con ella[/if][if flag=cuervo_sobrevive]la guardó bajo una columna caída, con un hechizo suyo encima[/if][if flag=cap4_sin_ritual]la tiene dormida todavía, pero no por mucho[/if]— pero se llevaron lo que podían. La ciudad ya no es.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'fracaso_corazon', valor: true },
      { kind: 'stat', stat: 'vida', delta: 10 },
    ],
    opciones: [
      {
        id: 'cap4_volver_herido',
        texto: 'Volver a la villa. Herido.',
        destino: 'cap4_retorno_villa',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap4_huida_corazon',
    capitulo: 4,
    background: { type: 'forest', hint: 'bosque al atardecer, escapando a pie' },
    texto: [
      'Escapás por atrás. El ruido de pólvora te sigue durante una hora. Después cede. Llegás a un claro chico, donde se detienen, donde [if flag=sena_vuelve_con_tebo]Sena le da agua a Tebo[/if][if noflag=sena_vuelve_con_tebo]alguien te da agua[/if] y te explica lo evidente: perdieron la ciudad pero ganaron tiempo.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'huida_corazon', valor: true }],
    opciones: [
      {
        id: 'cap4_volver_escapando',
        texto: 'Volver a la villa. A paso lento.',
        destino: 'cap4_retorno_villa',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  ACTO V — El pacto final
  // ===========================================================================
  {
    id: 'cap4_retorno_villa',
    capitulo: 4,
    titulo: 'La villa, diferente',
    background: { type: 'village', hint: 'villa de noche, fogones encendidos con más brasa que antes' },
    texto: [
      'Volvés a la villa tres días después. La villa ya no es la misma: no porque haya cambiado ella, sino porque vos la mirás distinto. La mirás sabiendo que Reyes viene. Sabiendo que la piedra [if flag=cuervo_contenedor]está dormida con el Cuervo y no puede volver por mil años[/if][if flag=cuervo_sobrevive]está dormida sin muerto, lo que ya es un milagro[/if][if flag=cap4_sin_ritual]está dormida sin contenedor y puede despertarse en cualquier momento, pronto[/if].',
      'Náyade está en la pasarela central. Junto a ella está [if flag=karn_rescatado]Orón[/if][if flag=mara_aliada]Mara[/if][if noflag=karn_rescatado]una carpincha vieja que no conocés[/if]. Detrás, en semicírculo, otros cuatro o cinco.',
      '—Contanos todo —te dice Náyade—. Esta noche decidimos qué facciones convocar. Mañana a la madrugada mandamos mensajeros. En tres días, en el Corazón —lo que queda del Corazón— se reúnen. O ahí decidimos la última pelea, o ahí nos rendimos todos juntos.',
    ],
    opciones: [
      {
        id: 'cap4_contar_todo',
        texto: 'Contar todo.',
        destino: 'cap4_fin',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap4_fin',
    capitulo: 4,
    titulo: 'Fin del Capítulo 4',
    background: { type: 'village', hint: 'villa al amanecer, mensajeros partiendo en distintas direcciones' },
    texto: [
      'Contás. Contás lo del archivo, lo del contenedor, lo del Cuervo, lo del Reyes. No dejás nada afuera —salvo el recuerdo que perdiste, que ya no podés contar porque ya no lo tenés—. Náyade escucha con una quietud que aprendiste a leer: quietud de quien está recalibrando, no de quien se rinde.',
      'Al amanecer, los mensajeros parten. Uno hacia los Guardianes del Fuego. Uno hacia los Acantilados Olvidados. Uno hacia el sur —donde [if flag=sena_al_sur]Sena prometió recibir a los que huyan[/if][if flag=sena_vuelve_con_tebo]los capibaras exiliados por los viejos conflictos quedan todavía[/if]—. Uno hacia lo que queda de La Sombra: a entregarle la decisión de si se suman o se pierden.',
      '[if flag=pipo_rescatado]Pipo, en tus brazos, duerme. Lleva dos noches durmiendo sin parpadear. Eso no es dormir. Es otra cosa. Pero respira. Y su respiración es la tuya, y a veces en la medianoche creés que es la única que te queda.[/if]',
      '[if flag=cuervo_contenedor]El Cuervo ya no vuelve. Su despedida fue la última que te dio alguien en este capítulo. Vas a tener que aprender a no esperarlo.[/if][if flag=cuervo_sobrevive]El Cuervo duerme en una choza que le prestó el Clan del Río: aliado improbable, invitado incómodo, viejo útil.[/if]',
      '[whisper](Fin del Capítulo 4 — "El ojo abierto". El Capítulo 5 es el último.)[/whisper]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap4_completado', valor: true },
      { kind: 'xp', cantidad: 180 },
    ],
    opciones: [
      {
        id: 'cap4_entrar_cap5',
        texto: 'Cerrar los ojos. Entrar al Capítulo 5.',
        destino: 'cap5_intro',
        intencion: 'neutral',
      },
    ],
  },
];
